// src/services/staffFteReportService.ts
//
// Builds a per-staff FTE ("time fraction") list for a chosen semester.
// Each row carries the staff member's CONTRACTED FTE (straight off the
// staff record) alongside two effective FTE figures derived from their
// released load letters for that semester:
//
//   - teachingFte = teaching_minutes / full-time base   (classroom load only)
//   - totalFte    = allocated_minutes / full-time base  (teaching + allowances)
//
// In the load-letter data, allocated_minutes = teaching_minutes +
// allowance_minutes, so totalFte reflects a staff member's whole timetabled
// commitment (POL, coordination, mentoring, etc. included) while teachingFte
// isolates time in front of a class. Both are null for staff without a
// released letter for the semester (shown as "—").

import { supabase } from '../supabase/config';
import { staffService } from './staffService';

// Full-time teaching minutes per fortnight. Mirrors the default used across
// the load-calculation code (see src/types/staff.ts).
const FULL_TIME_FORTNIGHT_MINUTES = 2220;

export type FteSemester = 'semester1' | 'semester2';

export interface StaffFteRow {
  staffId: string;
  staffName: string;
  staffCode: string;
  department: string;
  employmentType: string;
  contractedFte: number;
  // Minutes for the semester, summed across the staff member's released
  // letters. null when they have no released letter.
  teachingMinutes: number | null;
  allowanceMinutes: number | null;
  allocatedMinutes: number | null; // teaching + allowance
  // FTEs derived from the minutes above (2dp). null when no letter.
  teachingFte: number | null; // teaching only
  allowanceFte: number | null; // allowances only (teachingFte + allowanceFte = totalFte)
  totalFte: number | null; // teaching + allowances
  // totalFte - contractedFte, rounded to 2dp. null when no letter.
  fteDelta: number | null;
  hasLetter: boolean;
}

export interface StaffFteListOptions {
  year: number;
  semester: FteSemester;
  department?: string; // undefined or 'all' => no filter
  activeOnly?: boolean; // default true
}

const toFte = (minutes: number, fullTime: number): number =>
  fullTime > 0 ? Math.round((minutes / fullTime) * 100) / 100 : 0;

/**
 * One row per staff member with contracted, teaching, and total FTE for the
 * chosen semester. Every matching staff member is returned, whether or not
 * they have a released load letter — the effective columns are null for
 * those who don't.
 */
export async function getStaffFteList(
  options: StaffFteListOptions
): Promise<StaffFteRow[]> {
  const { year, semester, department, activeOnly = true } = options;

  // 1. Staff universe (the report lists everyone, not just letter-holders).
  const allStaff = await staffService.getAllStaff();
  const staffList = allStaff.filter(s => {
    if (activeOnly && s.status !== 'active') return false;
    if (department && department !== 'all' && s.department !== department) {
      return false;
    }
    return true;
  });

  // 2. Released letters for the chosen semester/year -> minutes per staff
  //    member. Sum across letters in case a staff member has more than one
  //    current letter for the semester (matches Timetabled Subs).
  const { data, error } = await supabase
    .from('load_letters')
    .select('staff_id, teaching_minutes, allowance_minutes, allocated_minutes, status, semester')
    .eq('academic_year', String(year))
    .eq('semester', semester)
    .is('superseded_at', null)
    .in('status', ['released', 'acknowledged', 'queried']);

  if (error) throw error;

  interface Mins {
    teaching: number;
    allowance: number;
    allocated: number;
  }
  const minsByStaff = new Map<string, Mins>();
  for (const row of data || []) {
    const existing = minsByStaff.get(row.staff_id) ?? {
      teaching: 0,
      allowance: 0,
      allocated: 0,
    };
    existing.teaching += row.teaching_minutes || 0;
    existing.allowance += row.allowance_minutes || 0;
    existing.allocated += row.allocated_minutes || 0;
    minsByStaff.set(row.staff_id, existing);
  }

  // 3. Build rows.
  return staffList.map(s => {
    const contractedFte = s.fte ?? 0;
    const fullTime = s.base_minutes_per_fortnight || FULL_TIME_FORTNIGHT_MINUTES;
    const mins = minsByStaff.get(s.id);
    const hasLetter = mins != null;

    const teachingMinutes = hasLetter ? mins!.teaching : null;
    const allowanceMinutes = hasLetter ? mins!.allowance : null;
    const allocatedMinutes = hasLetter ? mins!.allocated : null;

    const teachingFte = teachingMinutes == null ? null : toFte(teachingMinutes, fullTime);
    const allowanceFte = allowanceMinutes == null ? null : toFte(allowanceMinutes, fullTime);
    const totalFte = allocatedMinutes == null ? null : toFte(allocatedMinutes, fullTime);
    const fteDelta =
      totalFte == null ? null : Math.round((totalFte - contractedFte) * 100) / 100;

    return {
      staffId: s.id,
      staffName: s.name,
      staffCode: s.staff_code || s.initial || '',
      department: s.department || 'Unknown',
      employmentType: s.employment_type || '—',
      contractedFte,
      teachingMinutes,
      allowanceMinutes,
      allocatedMinutes,
      teachingFte,
      allowanceFte,
      totalFte,
      fteDelta,
      hasLetter,
    };
  });
}
